/**
 * Type for all settings.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
import mitt, {Emitter} from 'mitt';

export interface Settings {
    version: number, // Version of the settings. If changed, and the users version of the settings do not fit, the local storage is reset.
    timestamp: number, // Unix timestamp when the settings were first saved. Used to check if the settings should be reset.
    settings: any // May contain any settings as needed. E.g. a key "plugins", and then settings for plugins.
}

/**
 * Global user settings persisted in the local storage.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class UserSettings {
    /**
     * Version of the settings. If changed, and the users version of the settings do not fit, the local storage is reset.
     */
    static readonly VERSION: number = 1;

    /**
     * Singleton instance.
     */
    private static _instance: UserSettings;

    /**
     * Main key in local storage to put settings into.
     */
    private _storageKey: string = 'sitepackage';

    /**
     * Contains the current settings.
     */
    private _settings: Settings | null = null;

    /**
     * Event for checking if the settings have changed.
     */
    private _changeEvent: Emitter<{ change: Settings }> = mitt<{ change: Settings }>();

    static getInstance(): UserSettings {
        if (UserSettings._instance === undefined) {
            UserSettings._instance = new UserSettings();
        }
        return UserSettings._instance;
    }

    private constructor() {
        this.settings = this.get();
    }

    private get storageKey(): string {
        return this._storageKey;
    }

    private get settings(): Settings | null {
        return this._settings;
    }

    private set settings(settings: Settings | null) {
        this._settings = settings;
    }

    get changeEvent(): Emitter<{ change: Settings }> {
        return this._changeEvent;
    }

    /**
     * Gets the settings from the local storage.
     *
     * @returns {Settings}
     */
    get(): Settings {
        let settings: Settings | null = null;

        // No settings exist yet
        const item = localStorage.getItem(this.storageKey);
        if (!item) {
            settings = this.reset();
        }

        // Try to fetch existing settings from the local storage
        else {
            try {
                if (item) {
                    settings = JSON.parse(item);
                }

                // Sanity check
                if (settings !== null && !settings.hasOwnProperty('version')) {
                    settings = this.reset();
                }
            } catch (e) {
                settings = this.reset();
            }
        }

        // If no settings could be retrieved, throw an error
        if (settings === null) {
            throw new Error('Could not fetch settings.');
        }

        // Check if the settings version is equal to the current version. If no, reset the settings.
        if (settings.version !== UserSettings.VERSION) {
            settings = this.reset();
        }

        return settings;
    }

    /**
     * Sets new settings in the local storage.
     *
     * @param {Settings} settings New settings
     */
    set(settings: Settings): void {
        if (localStorage.getItem(this.storageKey) === undefined || !localStorage.getItem(this.storageKey)) {
            this.reset();
        }

        localStorage.setItem(this.storageKey, JSON.stringify(settings));
        this.settings = settings;
        this.changeEvent.emit('change', this.settings);
    }

    /**
     * Gets default settings.
     */
    getDefaultSettings(): Settings {
        return {
            'version': UserSettings.VERSION,
            'timestamp': (new Date()).getTime(),
            'settings': {}
        };
    }

    /**
     * Resets the user settings in the browsers local storage.
     */
    reset(): Settings {
        const settings = this.getDefaultSettings();
        localStorage.setItem(this.storageKey, JSON.stringify(settings));
        this.settings = settings;
        this.changeEvent.emit('change', this.settings);
        return settings;
    }

    /**
     * Deletes the user settings from the browsers local storage.
     */
    delete(): Settings {
        const settings = this.getDefaultSettings();
        localStorage.removeItem(this.storageKey);
        this.settings = this.getDefaultSettings();
        this.changeEvent.emit('change', this.settings);
        return settings;
    }
}
